import Axios from 'axios'
import { getToken, setToken, getUser } from '@/services/auth.service'

const axios = Axios.create({ baseURL: process.env.VUE_APP_API_URL })

export default class ApiService {
  constructor() {
    this.axios = axios
    this._token = getToken()
    this._user = getUser()
    if (this._token) {
      this.axios.defaults.headers.common.Authorization = `Bearer ${this._token}`
    }
  }

  get socket() {
    return this._socket
  }

  get token() {
    return this._token
  }

  set token(token) {
    this._token = token
    this.axios.defaults.headers.common.Authorization = `Bearer ${this._token}`
    setToken(this._token)
  }

  loginWithEmailAndPassword(email, password) {
    return new Promise((resolve, reject) => {
      axios
        .post('/auth/login', { email, password })
        .then(response => {
          if (!response) {
            throw new Error(
              "We couldn't find a user with that email/password combo."
            )
          }
          const {
            data: { token, user }
          } = response
          this.token = token
          resolve({ user, token })
        })
        .catch(err => reject(err))
    })
  }

  requestPasswordResetWithEmail(email) {
    return new Promise((resolve, reject) => {
      axios
        .post('/users/forgot', { email })
        .then(response => {
          if (!response) {
            throw new Error(
              'There was an error with your request. Please contact your super user.'
            )
          }
        })
        .catch(err => reject(err))
    })
  }

  generateOTP(token) {
    return new Promise((resolve, reject) => {
      axios
        .post('/users/forgot/newotp', { token })
        .then(response => {
          if (!response) {
            throw new Error(
              'There was an error with your request. Please contact your super user.'
            )
          }
        })
        .catch(err => reject(err))
    })
  }

  validateOTP(otp) {
    return new Promise((resolve, reject) => {
      axios
        .post('/users/forgot/otp', { otp })
        .then(response => {
          const {
            data: { isValid }
          } = response
          resolve({ isValid })
          if (!response) {
            throw new Error(
              'There was an error with your request. Please contact your super user.'
            )
          }
        })
        .catch(err => reject(err))
    })
  }

  processPasswordReset(token, password) {
    return new Promise((resolve, reject) => {
      axios
        .post('/users/forgot/reset', { token, password })
        .then(response => {
          if (!response) {
            throw new Error(
              'User validation failed: password: Password must be at least 8 characters long and have at least one number, one uppercase character, and one symbol.'
            )
          }
          resolve()
        })
        .catch(err => {
          reject(err)
        })
    })
  }

  registerNewUser(user) {
    return new Promise((resolve, reject) => {
      axios
        .post('/auth/register', { ...user })
        .then(response => {
          const {
            data: { token, user }
          } = response
          this.token = token
          resolve({ user, token })
        })
        .catch(err => reject(err))
    })
  }

  createNewUser(user) {
    return axios.post('/users', user)
  }

  updateUser(user) {
    return axios.put(`/users/${user._id}`, user)
  }

  fetchUsers() {
    return axios.get('/users')
  }

  fetchOrganisations() {
    return axios.get('/organisations')
  }

  fetchLicenses() {
    return axios.get('/super/licenses')
  }

  fetchAllUsers() {
    return axios.get('/super/users')
  }

  createUser(user) {
    return axios.post('/super/users', user)
  }

  createOrganisation(payload) {
    return axios.post('/super/organisations', payload)
  }

  editOrganisation(payload) {
    return axios.put(
      '/organisations/update/' + payload.organisation._id,
      payload
    )
  }

  fetchPlans() {
    return axios.get('/plans/all')
  }

  fetchAssignablePlans() {
    return axios.get('/plans/assign')
  }

  fetchUserPlans() {
    return axios.get('/plans')
  }

  fetchAssignments() {
    return axios.get('/assignments')
  }

  fetchNationalBenchmarkData(startDate, endDate) {
    return axios.get('/benchmarks/all', {
      params: {
        startDate,
        endDate
      }
    })
  }

  fetchClientBenchmarkData(startDate, endDate) {
    return axios.get('/benchmarks/us', {
      params: {
        startDate,
        endDate
      }
    })
  }

  fetchRegionalBenchmarkData(startDate, endDate) {
    return axios.get('/benchmarks/region', {
      params: {
        startDate,
        endDate
      }
    })
  }

  fetchNeighbourBenchmarkData(startDate, endDate) {
    return axios.get('/benchmarks/neighbour', {
      params: {
        startDate,
        endDate
      }
    })
  }

  fetchAvailabilityBenchmarkData() {
    return axios.get('/benchmarks/availability')
  }

  fetchBenchmarkPDF(comparisonFilter, startDate, endDate) {
    return axios.get(
      `/benchmarks/export?comparisonFilter=${comparisonFilter}&startDate=${startDate}&endDate=${endDate}`,
      {
        responseType: 'blob'
      }
    )
  }

  fetchSummaryPDF(assignmentId) {
    return axios.get(`/audits/summary/${assignmentId}`, {
      responseType: 'blob'
    })
  }

  fetchAllAssignments() {
    return axios.get('/assignments/all')
  }

  userForgotPassword(email) {
    return axios.post('/users/forgot', { email })
  }

  createAssignment(planId, userId, deadline) {
    return axios.post('/assignments/', {
      planId,
      userId,
      deadlineDate: deadline
    })
  }

  createBulkAssignments(assignments) {
    return axios.post('/assignments/', assignments)
  }

  updateAssignment(id, updates) {
    return axios.put(`/assignments/${id}`, updates)
  }

  archiveAssignment(id) {
    return axios.delete(`/assignments/${id}`)
  }

  getUsersAudits() {
    return axios.get('/audits')
  }

  getOrganisationAudits() {
    return axios.get('/audits/organisation')
  }

  getAuditForAssignment(id) {
    return axios.get('/')
  }

  setBeginAudit(id) {
    return axios.post(`/audits/start/${id}`)
  }

  updateAudit(auditId, updates) {
    return axios.put(`/audits/${auditId}`, updates)
  }

  dedupeSection(auditId, section) {
    return axios.post(`/audits/dedupeSection/${auditId}`, section)
  }

  continueAudit(assignmentId) {
    return axios.post(`/audits/continue/${assignmentId}`)
  }

  setAuditComplete(id, outcome) {
    return axios.post(`/audits/complete/${id}`, { outcome })
  }

  updatePlan(id, updates) {
    return axios.put(`/plans/${id}`, { updates })
  }

  uploadPlan(plans) {
    return axios.post('/plans', plans)
  }

  archivePlan(id) {
    return axios.delete(`/plans/${id}`)
  }

  findCompany(query, items = 5) {
    return axios.get(`/search/company?q=${query}&items=${items}`)
  }

  getReportForAuditsInRange(startDate, endDate) {
    return axios.get(`/audits/export?start=${startDate}&end=${endDate}`)
  }

  getOurBenchmarkData() {
    return axios.get('/benchmarks/us')
  }
}
